import type { ExploreFiltersQuery } from '~/components/VideoExplore/useExploreFilters';

import {
	SHORTS_ROUTE_SYNC_PRESERVED_LISTING_QUERY_KEYS,
	shortsFiltersToListingQuery,
	shortsListingQueryToFilters
} from '~/composables/useShortsFilters/featuredVideosFilterRules';

interface UseShortsRouteSyncOptions {
	currentCommercialId: ComputedRef<string>;
	toQueryParams: () => ExploreFiltersQuery;
}

export function useShortsRouteSync({
	currentCommercialId,
	toQueryParams
}: UseShortsRouteSyncOptions) {
	const route = useRoute();

	const routeCommercialId = computed(() => {
		const params = route.params as Record<
			string,
			string | string[] | undefined
		>;
		const value = params.commercialId;
		if (Array.isArray(value)) return value[0] ?? '';
		return value ?? '';
	});

	function preserveListingContextInQuery(query: Record<string, string>): void {
		const listingQuery = shortsFiltersToListingQuery(
			shortsListingQueryToFilters(route.query)
		);

		for (const key of SHORTS_ROUTE_SYNC_PRESERVED_LISTING_QUERY_KEYS) {
			const value = listingQuery[key];
			if (query[key] === undefined && value) query[key] = value;
		}
	}

	function syncRouteToCurrentItem(): void {
		if (!import.meta.client) return;

		const commercialId = currentCommercialId.value;
		if (!commercialId) return;

		const query = toQueryParams() as Record<string, string>;
		preserveListingContextInQuery(query);
		const search = new URLSearchParams(
			Object.fromEntries(Object.entries(query).filter(([, value]) => value))
		).toString();
		const url = `/shorts/${commercialId}${search ? `?${search}` : ''}`;

		try {
			window.history.replaceState(window.history.state, '', url);
		} catch (error) {
			console.error('[useShortsRouteSync] history.replaceState failed:', {
				error: error instanceof Error ? error.message : String(error),
				path: `/shorts/${commercialId}`,
				commercialId,
				timestamp: new Date().toISOString()
			});
		}
	}

	return {
		routeCommercialId,
		syncRouteToCurrentItem
	};
}
